<template>
  <div class="signup-page">
    <div class="left-side">
      <a @click="handleBackClick" class="back-link">&lt; back</a>
      <img
        :src="theme.current.loginImage"
        alt="Captain Logo"
        class="captain-logo"
      />
    </div>
    <div class="right-side">
      <img
        :src="theme.current.loginImage"
        alt="Captain Logo"
        class="captain-logo-mobile"
      />
      <!-- a @click="handleBackClick" class="back-link">&lt; back</a -->
      <h1>Account Registration</h1>
      <p>Become a member to chat with Olivia</p>
      <Form @submit="handleSignUp">
        <div v-if="showPageOne">
          <div>
            <label for="firstName">First Name</label>
            <input type="text" id="firstName" v-model="firstName" />
            <span class="error-message">{{ validationErrors.firstName }}</span>
          </div>
          <div>
            <label for="lastName">Last Name</label>
            <input type="text" id="lastName" v-model="lastName" />
            <span class="error-message">{{ validationErrors.lastName }}</span>
          </div>
          <div>
            <label for="username">Email Address</label>
            <input type="text" id="username" v-model="username" />
            <span class="error-message">{{ validationErrors.username }}</span>
          </div>
          <div>
            <label for="password">Password</label>
            <input type="password" id="password" v-model="password" />
            <span class="error-message">{{ validationErrors.password }}</span>
          </div>
          <button type="button" @click="validateFirstPage">Continue</button>
        </div>

        <div v-if="showPageTwo">
          <div class="phone-verification">
            <div class="phone-input-container">
              <label for="phoneNumber">Phone Number</label>
              <div class="input-with-button">
                <input
                  type="text"
                  id="phoneNumber"
                  v-model="phoneNumber"
                  placeholder="10-digit number"
                  :disabled="verificationSent || isVerified"
                />
                <button
                  type="button"
                  class="verify-btn"
                  @click="sendVerification"
                  :disabled="
                    !isValidPhoneNumber ||
                    verificationSent ||
                    isVerified ||
                    isLoading
                  "
                >
                  Verify
                </button>
              </div>
              <span class="error-message">{{ phoneNumberError }}</span>
            </div>

            <!-- Verification code input with timer -->
            <div
              v-if="verificationSent && !isVerified"
              class="verification-code-container"
            >
              <label for="verificationCode">Verification Code</label>
              <div class="input-with-button">
                <input
                  type="text"
                  id="verificationCode"
                  v-model="verificationCode"
                  placeholder="Enter code"
                  :disabled="isVerified || isLoading"
                />
                <button
                  type="button"
                  class="verify-btn"
                  @click="verifyCode"
                  :disabled="!verificationCode || isVerified || isLoading"
                >
                  Verify
                </button>
                <div class="timer">{{ formattedTime }}</div>
              </div>
            </div>

            <!-- Verification status message -->
            <div
              v-if="verificationMessage"
              :class="[
                'verification-message',
                {
                  success: isVerified,
                  error: !isVerified && verificationMessage,
                },
              ]"
            >
              {{ verificationMessage }}
            </div>
          </div>

          <div class="reminder-preferences">
            <div class="reminder-field">
              <label class="field-label">Text Reminders</label>
              <div
                class="segmented"
                role="radiogroup"
                aria-label="Text Reminders"
              >
                <input
                  type="radio"
                  id="su-text-yes"
                  value="yes"
                  v-model="textReminders"
                />
                <label for="su-text-yes">Yes</label>

                <input
                  type="radio"
                  id="su-text-no"
                  value="no"
                  v-model="textReminders"
                />
                <label for="su-text-no">No</label>
              </div>
            </div>

            <div class="reminder-field">
              <label class="field-label">Email Reminders</label>
              <div
                class="segmented"
                role="radiogroup"
                aria-label="Email Reminders"
              >
                <input
                  type="radio"
                  id="su-email-yes"
                  value="yes"
                  v-model="emailReminders"
                />
                <label for="su-email-yes">Yes</label>

                <input
                  type="radio"
                  id="su-email-no"
                  value="no"
                  v-model="emailReminders"
                />
                <label for="su-email-no">No</label>
              </div>
            </div>
          </div>

          <button
            type="submit"
            class="register-btn"
            :disabled="
              !isVerified || !textReminders || !emailReminders || isLoading
            "
          >
            Register
          </button>
        </div>
      </Form>
    </div>
    <div v-if="isLoading" class="loading-overlay">
      <div class="spinner"></div>
    </div>
  </div>
</template>

<script>
import { ref, reactive, computed, onBeforeUnmount } from "vue";
import { useForm, useField, Form } from "vee-validate";
import * as yup from "yup";
//import api from "@/services/auth_api";
import registrationApi from "@/services/registration_api";
import { useRouter } from "vue-router";
import { useSessionStore } from "@/stores/session";
import { useThemeStore } from "@/stores/theme";

export default {
  name: "SignUpPage",
  components: {
    Form,
  },
  setup() {
    const router = useRouter();
    const session = useSessionStore();

    const theme = useThemeStore();

    const schema = yup.object({
      firstName: yup.string().required("First Name is required"),
      lastName: yup.string().required("Last Name is required"),
      username: yup
        .string()
        .required("Email is required")
        .email("Must be a valid email"),
      password: yup
        .string()
        .required("Password is required")
        .min(6, "Password must be at least 6 characters"),
      phoneNumber: yup
        .string()
        .required("Phone Number is required")
        .matches(/^[0-9]{10}$/, "Phone Number must be 10 digits"),
    });

    const firstPageSchema = yup.object({
      firstName: yup.string().required("First Name is required"),
      lastName: yup.string().required("Last Name is required"),
      username: yup
        .string()
        .required("Email is required")
        .email("Email must be valid"),
      password: yup
        .string()
        .required("Password is required")
        .min(6, "Password must be at least 6 characters"),
    });

    const { handleSubmit } = useForm({
      validationSchema: schema,
    });

    const { value: firstName, errorMessage: firstNameError } =
      useField("firstName");
    const { value: lastName, errorMessage: lastNameError } =
      useField("lastName");
    const { value: username, errorMessage: usernameError } =
      useField("username");
    const { value: password, errorMessage: passwordError } =
      useField("password");
    const { value: phoneNumber, errorMessage: phoneNumberError } =
      useField("phoneNumber");
    const textReminders = ref("");
    const emailReminders = ref("");
    const verificationCode = ref("");
    const showPageOne = ref(true);
    const showPageTwo = ref(false);
    const verificationSent = ref(false);
    const isVerified = ref(false);
    const isLoading = ref(false);
    const verificationMessage = ref("");
    const timerSeconds = ref(180); // 3 minutes
    let timerInterval = null;

    const validationErrors = reactive({
      firstName: "",
      lastName: "",
      username: "",
      password: "",
    });
    // Computed properties
    const formattedTime = computed(() => {
      const minutes = Math.floor(timerSeconds.value / 60);
      const seconds = timerSeconds.value % 60;
      return `${minutes}:${seconds.toString().padStart(2, "0")}`;
    });

    const isValidPhoneNumber = computed(() => {
      return /^[0-9]{10}$/.test(phoneNumber.value);
    });

    const validateFirstPage = async () => {
      try {
        await firstPageSchema.validate(
          {
            firstName: firstName.value,
            lastName: lastName.value,
            username: username.value,
            password: password.value,
          },
          { abortEarly: false },
        );
        showPageOne.value = false;
        showPageTwo.value = true;
      } catch (err) {
        err.inner.forEach((error) => {
          validationErrors[error.path] = error.message;
        });
      }
    };

    const startTimer = () => {
      // Clear any existing timer
      clearInterval(timerInterval);

      // Reset timer value
      timerSeconds.value = 180;

      // Start new timer
      timerInterval = setInterval(() => {
        if (timerSeconds.value > 0) {
          timerSeconds.value--;
        } else {
          // Timer expired
          clearInterval(timerInterval);
          resetVerification();
          verificationMessage.value =
            "Verification code expired. Please try again.";
        }
      }, 1000);
    };
    const resetVerification = () => {
      verificationSent.value = false;
      verificationCode.value = "";
      clearInterval(timerInterval);
    };

    // Should work on verification logic
    const sendVerification = async () => {
      if (!isValidPhoneNumber.value) {
        verificationMessage.value =
          "Please enter a valid 10-digit phone number";
        return;
      }

      try {
        isLoading.value = true;
        const token = session.token;
        await registrationApi.sendVerificationCode(phoneNumber.value, token);

        verificationSent.value = true;
        verificationMessage.value =
          "Verification code sent! Please check your phone.";
        startTimer();
      } catch (error) {
        verificationMessage.value =
          error.response?.data?.message ||
          "Failed to send verification code. Please try again.";
      } finally {
        isLoading.value = false;
      }
    };

    const verifyCode = async () => {
      try {
        isLoading.value = true;
        const token = session.token;
        const result = await registrationApi.verifyCode(
          phoneNumber.value,
          verificationCode.value,
          token,
        );

        if (result.verified) {
          isVerified.value = true;
          clearInterval(timerInterval);
          verificationMessage.value = "Phone number verified successfully!";
        } else {
          verificationMessage.value =
            "Invalid verification code. Please try again.";
        }
      } catch (error) {
        verificationMessage.value =
          error.response?.data?.message ||
          "Failed to verify code. Please try again.";
      } finally {
        isLoading.value = false;
      }
    };
    const handleSignUp = handleSubmit(async () => {
      if (!isVerified.value) {
        verificationMessage.value =
          "Please verify your phone number before registering.";
        return;
      }

      try {
        isLoading.value = true;

        const userData = {
          first_name: firstName.value,
          last_name: lastName.value,
          email: username.value,
          password: password.value,
          phone_number: phoneNumber.value,
          text_reminders: textReminders.value === "yes",
          email_reminders: emailReminders.value === "yes",
          is_verified: isVerified.value,
        };
        //const response = await api.signUp(userData);
        const response = await registrationApi.register(
          userData,
          session.token,
        );

        // Then complete the registration with phone verification
        //await registrationApi.completeRegistration(userData, response.token);

        console.log("Registration successful:", response);
        router.push("/");
      } catch (error) {
        console.error("Error during sign up:", error);
        verificationMessage.value =
          error.response?.data?.message ||
          "Registration failed. Please try again.";
      } finally {
        isLoading.value = false;
      }
    });

    const handleBackClick = () => {
      if (showPageOne.value) {
        router.push("/"); // Navigate to landing page
      } else {
        showPageOne.value = true;
        showPageTwo.value = false;
        resetVerification();
      }
    }; // Clean up timer when component is unmounted
    onBeforeUnmount(() => {
      clearInterval(timerInterval);
    });

    return {
      theme,
      firstName,
      lastName,
      firstNameError,
      lastNameError,
      username,
      usernameError,
      password,
      passwordError,
      phoneNumber,
      phoneNumberError,
      textReminders,
      emailReminders,
      verificationCode,
      validateFirstPage,
      handleSignUp,
      handleBackClick,
      showPageOne,
      showPageTwo,
      validationErrors,
      verificationSent,
      isVerified,
      isLoading,
      verificationMessage,
      formattedTime,
      sendVerification,
      verifyCode,
      isValidPhoneNumber,
    };
  },
};
</script>

<style scoped>
.signup-page {
  display: flex;
  height: var(--app-height);
  min-height: 100vh;
  min-height: -webkit-fill-available;
  margin: 0;
  background: var(
    --background-color
  ); /* keeps bg consistent when content overflows */
}

.logo-text-overlay-image {
  position: absolute;
  top: 28%;
  left: 18%;
  width: 20%;
  height: 100px;
}

.rightside-overlay-image {
  position: absolute;
  top: 15px;
  right: 15px;
  width: 35%;
  height: 90%;
}

.left-side,
.right-side {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
}

.left-side {
  background-color: #e9e5d6;
}

.right-side {
  background-color: #e9e5d6;
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
}

.right-side > h1,
.right-side > p,
.right-side > form,
.right-side > div {
  width: 50%;
  max-width: 500px; /* mirrors PhoneVerificationPage */
  text-align: left;
}

.signup-page h1 {
  font-size: 2rem;
  color: #333333;
  margin-bottom: 0.5em;
}

.signup-page form {
  display: flex;
  flex-direction: column;
  align-items: left;
  /* Center align form elements */
  width: 50%;
  /* Make the form take 80% of the container width */
}

.signup-page div {
  margin-bottom: 1em;
  width: 100%;
  /* Ensure divs take full width */
}

.signup-page label {
  margin-bottom: 0.5em;
  color: #333333;
  width: 50%;
  text-align: left;
}

.signup-page input {
  padding: 0.5em;
  font-size: 1em;
  border: 1px solid #ccc;
  border-radius: 4px;
  width: 100%;
}

.signup-page button {
  padding: 0.7em;
  font-size: 1em;
  color: #fff;
  background-color: var(--primary-color);
  border: none;
  border-radius: 4px;
  cursor: pointer;
  width: 100%;
}

.signup-page button:hover {
  background-color: var(--primary-color);
}

.back-link {
  position: absolute;
  top: 20px;
  left: 40px;
  text-decoration: none;
  color: var(--primary-color);
  font-size: 20px;
}

.back-link:hover {
  text-decoration: underline;
}

.captain-logo-mobile {
  display: none;
  width: 50%;
  height: auto;
  margin-bottom: 1em;
}
/* Phone verification styles */
.phone-input-container {
  margin-bottom: 20px;
}

.input-with-button {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
}

.input-with-button input {
  flex: 3;
  min-width: 150px;
}

.verify-btn {
  background-color: #4caf50;
  min-width: 130px;
  white-space: nowrap;
  flex: 1; /* Give button less flex space */
  max-width: 150px; /* Limit button width */
}

.verify-btn:disabled {
  background-color: #cccccc;
  cursor: not-allowed;
}

.verification-code-container {
  margin-top: 15px;
  margin-bottom: 20px;
}

.timer {
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 60px;
  font-weight: bold;
  color: #ff5722;
}

.verification-message {
  margin: 10px 0;
  padding: 8px;
  border-radius: 4px;
  text-align: center;
}

.verification-message.success {
  background-color: #e8f5e9;
  color: #2e7d32;
}

.verification-message.error {
  background-color: #ffebee;
  color: #c62828;
}

.register-btn {
  background-color: #1976d2;
}

.register-btn:disabled {
  background-color: #cccccc;
  cursor: not-allowed;
}

/* Loading overlay */
.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #3498db;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

/* SignUp reminder chips — align with PhoneVerificationPage */
.reminder-preferences {
  display: flex;
  flex-direction: column;
  gap: 15px;
  margin-top: 12px;
}

.field-label {
  display: block;
  margin-bottom: 8px;
  font-weight: bold;
  color: #333;
}

.segmented {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
}

/* Hide native radios */
.segmented input[type="radio"] {
  position: absolute;
  opacity: 0;
  pointer-events: none;
}

/* Chip base */
.segmented label {
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  background: #f8fafc;
  cursor: pointer;
  padding: 12px 14px;
  font-size: 16px;
  font-weight: 400;
  min-height: 48px;
  user-select: none;
  color: #555;
}

/* Selected */
.segmented input[type="radio"]:checked + label {
  background: #e3f2fd;
  color: #1976d2;
  border-color: #90caf9;
  box-shadow: inset 0 0 0 2px rgba(25, 118, 210, 0.12);
}

/* Focus */
.segmented label:focus,
.segmented input[type="radio"]:focus + label {
  outline: 2px solid #90caf9;
  outline-offset: 2px;
}

.segmented label:active {
  transform: scale(0.98);
}

/* Slight bump for coarse pointers if you want */
@media (pointer: coarse) {
  .segmented label {
    min-height: 52px;
  }
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

/* Make sure the button styling remains consistent */
button {
  padding: 0.7em;
  font-size: 1em;
  color: #fff;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  width: 100%;
}
/* Media query to hide the left-side div on mobile devices */
@media (max-width: 600px) {
  .left-side {
    display: none;
  }

  .right-side {
    flex: 1;
    width: 100%;
    padding: 0 1.5em;
  }

  .right-side h1,
  .right-side p,
  .right-side form,
  .right-side div {
    width: 100%; /* Take up full width on mobile */
    max-width: none;
    text-align: left;
  }

  .signup-page {
    padding: 0;
    margin: 0;
    width: 100%;
  }

  h1 {
    font-size: 22px;
  }

  p {
    font-size: 16px;
  }

  label {
    font-size: 16px;
  }

  input {
    font-size: 16px;
    padding: 12px;
  }

  button {
    padding: 12px;
    font-size: 16px;
  }

  .back-link {
    margin-bottom: 15px;
    position: relative;
    top: 10px;
    left: 10px;
  }

  form div {
    margin-bottom: 16px;
  }

  .captain-logo-mobile {
    display: block;
    width: 70%; /* Larger logo */
    max-width: 200px;
    height: auto;
    margin-bottom: 1em;
    margin-top: 15%;
  }
  .input-with-button {
    flex-direction: column;
    align-items: stretch;
  }

  .input-with-button input {
    width: 100%;
    margin-bottom: 10px;
  }

  .verify-btn {
    width: 100%;
    max-width: none;
    padding: 12px;
  }
  .verification-code-container {
    margin-top: 20px;
    margin-bottom: 20px;
  }

  .timer {
    font-size: 18px;
  }

  /* Make verification message more prominent */
  .verification-message {
    padding: 12px;
    font-size: 16px;
  }
}
</style>
